import { User } from '@core/core.state';
import { ResourceState, ResourceStatuses, SafeResourceState } from '@platform/shared/resource.state';

export interface AbacusInboxes {
  creditor: ResourceState<any>;
  payroll: ResourceState<any>;
}

export const PORTAL_TASK_STATUS_ARRAY = ['Open', 'InProgress', 'Denied', 'Done'];

export type PortalTaskStatus = 'Open' | 'InProgress' | 'Denied' | 'Done';
export type PortalTaskPriority = 'Normal' | 'High';
export type SourceSystem = 'Portal' | 'Abacus' | 'Sharepoint';

export interface TaskOverview {
  id: string;
  title: string;
  description: string;
  status: PortalTaskStatus;
  priority: PortalTaskPriority;
  source: SourceSystem;
  assignees: TaskUserDto[];
  dueOn?: Date;
  createdBy: TaskUserDto;
  createdOn: Date;
  modifiedBy: TaskUserDto;
  modifiedOn: Date;
  parentId: string;
  customerSpaceName: string;
  myAbacusInboxUrl: string;
  projectName: string;
  completed: boolean;
  sharePointTaskUrl: string;
}

export interface TaskUserDto {
  userId: string;
  name: string;
}

export interface PortalTask {
  id: string;
  title: string;
  description: string;
  status: PortalTaskStatus;
  priority: PortalTaskPriority;
  source: SourceSystem;
  assignedTo: User;
  dueOn: Date;
  createdBy: User;
  createdOn: Date;
  modifiedBy: User;
  modifiedOn: Date;
  parentId: string;
  customerSpaceName: string;
  myAbacusInboxUrl: string;
  projectName: string;
  descriptionTemplateId?: string;
}

export interface PortalTaskComment {
  id: string;
  taskId: number;
  text: string;
  createdBy: User;
  createdOn: Date;
}

export interface PortalTaskDocument {
  id: string;
  taskId: string;
  name: string;
  content: string;
  size: string;
}

export type DocumentInboxStatus = 'Pending' | 'Processing' | 'Transmitted';

export interface DocumentUploadHistory {
  documentName: string;
  documentType: string;
  uploadDateTime: Date;
  uploadedBy: string;
  status: DocumentInboxStatus;
  isDownloadAvailable: boolean;
}

export interface DocumentUploadHistoryList {
  entries: ResourceState<DocumentUploadHistory[]>;
  documentDownload: ResourceState<Blob>;
}

export interface PortalTasksState {
  widget: ResourceState<TaskOverview[]>;
  allTasksForUser: SafeResourceState<TaskOverview[]>;
  allCustomerSpacesTasksForUser: ResourceState<TaskOverview[]>;
  taskDetail: SafeResourceState<PortalTask>;
  taskComments: ResourceState<PortalTaskComment[]>;
  taskDocuments: ResourceState<PortalTaskDocument[]>;
}

export interface FiduciaryState {
  abacusInboxes: AbacusInboxes;
  portalTasks: PortalTasksState;
  inboxUploadHistory: DocumentUploadHistoryList;
}

export const initialFiduciaryState: FiduciaryState = {
  abacusInboxes: {
    creditor: {
      data: null!,
      status: ResourceStatuses.NONE,
      error: null!,
    },
    payroll: {
      data: null!,
      status: ResourceStatuses.NONE,
      error: null!,
    },
  },
  portalTasks: {
    widget: {
      data: [],
      status: ResourceStatuses.NONE,
      error: null!,
    },
    allTasksForUser: {
      data: null,
      status: ResourceStatuses.NONE,
      error: null,
    },
    allCustomerSpacesTasksForUser: {
      data: [],
      status: ResourceStatuses.NONE,
      error: null!,
    },
    taskDetail: {
      data: null!,
      status: ResourceStatuses.NONE,
      error: null!,
    },
    taskComments: {
      data: [],
      status: ResourceStatuses.NONE,
      error: null!,
    },
    taskDocuments: {
      data: [],
      status: ResourceStatuses.NONE,
      error: null!,
    },
  },
  inboxUploadHistory: {
    documentDownload: {
      data: null!,
      status: ResourceStatuses.NONE,
      error: null!,
    },
    entries: {
      data: [],
      status: ResourceStatuses.NONE,
      error: null!,
    },
  },
};

export const FIDUCIARY_STATE_FEATURE_KEY = 'fiduciary';
